import ApplicationViewModal from '@/components/sharedModals/applicationViewModal/ApplicationViewModal';
import CertificateViewModal from '@/components/sharedModals/CertificateViewModal';
import HistoryModal from '@/components/sharedModals/HistoryModal';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { gql, useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { Edit, Eye, FileCheck2, FileX, History, MessageSquare, RotateCcw, Send } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ApplicantSignatureModal from './ApplicantSignatureModal';
import EditDraftModal from './EditDraftModal';
import ViewRemarksModal from './ViewRemarksModal';

// GraphQL Queries and Mutations
const GET_CERTIFICATE = gql`
   query GetCertificatesByApplicationId($applicationId: ID!) {
      getCertificatesByApplicationId(applicationId: $applicationId) {
         id
         certificateNumber
         certificateStatus
         dateCreated
         dateIssued
         expiryDate
         dateApproved
         csawCertificateData {
            registrationType
            ownerName
            address
            purpose
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         ptprCertificateData {
            ownerName
            address
            contactNumber
            email
            octTctNumber
            lotNumber
            barangay
            municipality
            province
            totalLotArea
            areaDevotedToPlantation
            spacing
            treeSpeciesPlanted
            datePlanted
         }
         applicantSignature {
            data
            contentType
         }
         orderOfPayment {
            officialReceipt {
               orNumber
               amount
               dateIssued
            }
         }
      }
   }
`;

const UNDO_RENEWAL = gql`
   mutation UndoRenewal($applicationId: ID!) {
      undoRenewal(applicationId: $applicationId) {
         id
         status
         currentStage
      }
   }
`;

/**
 * ClientApplicationCard - A modern, user-friendly card component for displaying application status
 * Designed specifically for end-users/clients to track their applications
 */
const ClientApplicationCard = ({
   application,
   onDelete,
   onUnsubmit,
   onSubmit,
   onResubmit,
   onView,
   onEdit,
   onHistoryView,
   fetchCOVPermit,
   fetchCSAWPermit,
   fetchPLTCPPermit,
   fetchPTPRPermit,
   fetchPLTPPermit,
   fetchTCEBPPermit,
}) => {
   // Modal states
   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
   const [isViewModalOpen, setIsViewModalOpen] = useState(false);
   const [isRemarksModalOpen, setIsRemarksModalOpen] = useState(false);
   const [isViewCertModalOpen, setIsViewCertModalOpen] = useState(false);
   const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
   const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);

   // Add confirmation dialog states
   const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
   const [showUnsubmitConfirm, setShowUnsubmitConfirm] = useState(false);
   const [showResubmitConfirm, setShowResubmitConfirm] = useState(false);
   const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
   const [showUndoRenewalConfirm, setShowUndoRenewalConfirm] = useState(false);

   // GraphQL hooks
   const { loading: certLoading, error: certError, data: certData, refetch } = useQuery(GET_CERTIFICATE, {
      variables: { applicationId: application.id },
      skip: !isViewCertModalOpen && !isSignatureModalOpen
   });

   const [undoRenewal] = useMutation(UNDO_RENEWAL, {
      refetchQueries: ['GetUserApplications']
   });

   // Helper function for status badge styling
   const getStatusBadgeStyle = (status) => {
      switch (status.toLowerCase()) {
         case 'draft':
            return 'bg-gray-100 hover:bg-gray-100 text-gray-800 hover:text-gray-800 border border-gray-300';
         case 'submitted':
            return 'bg-blue-50 hover:bg-blue-50 text-blue-700 hover:text-blue-700 border border-blue-200';
         case 'in progress':
            return 'bg-yellow-50 hover:bg-yellow-50 text-yellow-700 hover:text-yellow-700 border border-yellow-200';
         case 'returned':
            return 'bg-orange-50 hover:bg-orange-50 text-orange-700 hover:text-orange-700 border border-orange-200';
         case 'accepted':
            return 'bg-emerald-50 hover:bg-emerald-50 text-emerald-700 hover:text-emerald-700 border border-emerald-200';
         case 'released':
            return 'bg-green-50 hover:bg-green-50 text-green-700 hover:text-green-700 border border-green-200';
         case 'expired':
            return 'bg-red-50 hover:bg-red-50 text-red-700 hover:text-red-700 border border-red-200';
         case 'renewed':
            return 'bg-purple-50 hover:bg-purple-50 text-purple-700 hover:text-purple-700 border border-purple-200';
         case 'rejected':
            return 'bg-red-50 hover:bg-red-50 text-red-700 hover:text-red-700 border border-red-200';
         default:
            return 'bg-gray-100 hover:bg-gray-100 text-gray-800 hover:text-gray-800 border border-gray-300';
      }
   };

   // Get actions based on application status
   const getActions = () => {
      const actions = [
         {
            icon: Eye,
            label: "View Application",
            onClick: () => setIsViewModalOpen(true),
            variant: "outline",
            className: "view-action"
         },
         {
            icon: History,
            label: "View History",
            onClick: () => setIsHistoryModalOpen(true),
            variant: "outline",
            className: "history-action text-purple-600"
         }
      ];

      // Add status-specific actions
      switch (application.status.toLowerCase()) {
         case 'draft':
            actions.push(
               {
                  icon: Edit,
                  label: "Edit Application",
                  onClick: () => setIsEditModalOpen(true),
                  variant: "outline",
                  className: "edit-action"
               },
               {
                  icon: FileX,
                  label: "Delete Draft",
                  onClick: () => setShowDeleteConfirm(true),
                  variant: "outline",
                  className: "delete-action text-red-600"
               },
               {
                  icon: Send,
                  label: "Submit Application",
                  onClick: () => setShowSubmitConfirm(true),
                  variant: "outline",
                  className: "submit-action text-green-600"
               }
            );
            break;
         case 'returned':
            actions.push(
               {
                  icon: Edit,
                  label: "Edit Application",
                  onClick: () => setIsEditModalOpen(true),
                  variant: "outline",
                  className: "edit-action"
               },
               {
                  icon: MessageSquare,
                  label: "View Remarks",
                  onClick: () => setIsRemarksModalOpen(true),
                  variant: "outline",
                  className: "remarks-action"
               },
               {
                  icon: Send,
                  label: "Resubmit Application",
                  onClick: () => setShowResubmitConfirm(true),
                  variant: "outline",
                  className: "resubmit-action text-green-600"
               }
            );
            break;
         case 'submitted':
            actions.push({
               icon: RotateCcw,
               label: "Unsubmit Application",
               onClick: () => setShowUnsubmitConfirm(true),
               variant: "outline",
               className: "unsubmit-action text-yellow-600"
            });
            break;
      }

      // Add certificate view action for Released or Expired status
      if (application.status === 'Released' || application.status === 'Expired') {
         actions.push({
            icon: FileCheck2,
            label: "View Certificate",
            onClick: () => setIsViewCertModalOpen(true),
            variant: "outline",
            className: "certificate-action text-blue-600"
         });
      }

      // Update undo renewal action
      if (application.status === 'Renewed') {
         actions.push({
            icon: RotateCcw,
            label: "Undo Renewal",
            onClick: () => setShowUndoRenewalConfirm(true),
            variant: "outline",
            className: "undo-renewal-action text-orange-600"
         });
      }

      // Add signature action for Released PTPR certificates
      if (application.status === 'Released' && application.applicationType === 'Private Tree Plantation Registration') {
         actions.push({
            icon: Edit,
            label: "Sign Certificate",
            onClick: () => setIsSignatureModalOpen(true),
            variant: "outline",
            className: "signature-action text-blue-600"
         });
      }

      return actions;
   };

   const isMobile = useMediaQuery('(max-width: 640px)');
   const navigate = useNavigate();
   const location = useLocation();

   // Format the date using the timestamp format
   const formatDate = (timestamp) => {
      try {
         if (!timestamp) return 'Not submitted';
         if (typeof timestamp === 'string') {
            if (!isNaN(timestamp)) {
               return format(new Date(parseInt(timestamp)), 'MMM d, yyyy');
            }
            return format(new Date(timestamp), 'MMM d, yyyy');
         }
         if (typeof timestamp === 'number') {
            return format(new Date(timestamp), 'MMM d, yyyy');
         }
         if (timestamp instanceof Date) {
            return format(timestamp, 'MMM d, yyyy');
         }
         return 'Invalid date';
      } catch (error) {
         console.error('Error formatting date:', error, 'timestamp:', timestamp);
         return 'Invalid date';
      }
   };
   const handleAction = (action, event) => {
      event.preventDefault();
      const currentParams = new URLSearchParams(location.search);

      switch (action) {
         case 'view_application':
            setIsViewModalOpen(true);
            break;
         case 'edit_application':
            setIsEditModalOpen(true);
            break;
         case 'delete_draft':
            setShowDeleteConfirm(true);
            break;
         case 'submit_application':
            setShowSubmitConfirm(true);
            break;
         case 'unsubmit_application':
            setShowUnsubmitConfirm(true);
            break;
         case 'resubmit_application':
            setShowResubmitConfirm(true);
            break;
         case 'view_history':
            setIsHistoryModalOpen(true);
            break;
         case 'view_remarks':
            setIsRemarksModalOpen(true);
            break;
         case 'view_certificate':
            setIsViewCertModalOpen(true);
            break;
         case 'sign_certificate':
            setIsSignatureModalOpen(true);
            break;
         case 'undo_renewal':
            setShowUndoRenewalConfirm(true);
            break;
         default:
            break;
      }

      // Update URL for status changes
      if (action === 'submit_application') {
         currentParams.set('status', 'Submitted');
         navigate({ search: currentParams.toString() });
      } else if (action === 'unsubmit_application') {
         currentParams.set('status', 'Draft');
         navigate({ search: currentParams.toString() });
      } else if (action === 'resubmit_application') {
         currentParams.set('status', 'Submitted');
         navigate({ search: currentParams.toString() });
      }
   };

   // Render action buttons with tooltips
   const renderActionButtons = () => {
      const actions = getActions();

      return (
         <div className="space-y-2">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Actions:</p>
            <div className="flex flex-wrap gap-2">
               {actions.map((action, index) => (
                  <TooltipProvider key={index} delayDuration={200}>
                     <Tooltip>
                        <TooltipTrigger asChild>
                           <Button
                              variant={action.variant}
                              size="icon"
                              onClick={action.onClick}
                              className={`${action.className} h-9 w-9 p-0`}
                           >
                              <action.icon className="h-4 w-4" />
                           </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                           <p>{action.tooltip || action.label}</p>
                        </TooltipContent>
                     </Tooltip>
                  </TooltipProvider>
               ))}
            </div>
         </div>
      );
   };

   const handleEditSave = (editedData) => {
      onEdit(application.id, editedData);
      setIsEditModalOpen(false);
   };

   return (
      <>
         <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 bg-white dark:bg-black dark:border-gray-700">
            {/* Status Banner */}
            <div className={`h-2 ${getStatusBadgeStyle(application.status)}`} />

            <CardContent className="p-6">
               {/* Header Section */}
               <div className="flex justify-between items-start mb-4">
                  <div>
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {application.applicationNumber}
                     </h3>
                     <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {application.applicationType}
                     </p>
                  </div>
                  <Badge className={`${getStatusBadgeStyle(application.status)} px-3 py-1 dark:border-opacity-50`}>
                     {application.status}
                  </Badge>
               </div>

               {/* Details Section */}
               <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                     <div>
                        <p className="text-sm font-bold text-gray-500 dark:text-gray-400">Request Type</p>
                        <p className="mt-1 dark:text-gray-300">{application.requestType || "N/A"}</p>
                     </div>
                     <div className="text-right">
                        <p className="text-sm font-bold text-gray-500 dark:text-gray-400">Submitted On</p>
                        <p className="mt-1 dark:text-gray-300">
                           {application.dateOfSubmission ? formatDate(application.dateOfSubmission) : 'Not submitted'}
                        </p>
                     </div>
                  </div>

                  {/* {application.currentStage && (
                     <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Current Stage</p>
                        <p className="mt-1 dark:text-gray-300">{application.currentStage}</p>
                     </div>
                  )} */}

                  {application.remarks && (
                     <div>
                        <p className="text-sm font-bold text-gray-500 dark:text-gray-400">Latest Remarks</p>
                        <p className="mt-1 text-sm dark:text-gray-300">{application.remarks}</p>
                     </div>
                  )}
               </div>

               {/* Actions Section */}
               {renderActionButtons()}
            </CardContent>
         </Card>

         {/* Modals */}
         <EditDraftModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSave={handleEditSave}
            application={application}
            fetchCOVPermit={fetchCOVPermit}
            fetchCSAWPermit={fetchCSAWPermit}
            fetchPLTCPPermit={fetchPLTCPPermit}
            fetchPTPRPermit={fetchPTPRPermit}
            fetchPLTPPermit={fetchPLTPPermit}
            fetchTCEBPPermit={fetchTCEBPPermit}
         />
         <ApplicationViewModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            application={application}
         />
         <ViewRemarksModal
            isOpen={isRemarksModalOpen}
            onClose={() => setIsRemarksModalOpen(false)}
            application={application}
         />
         {(application.status === 'Released' || application.status === 'Expired') && (
            <CertificateViewModal
               isOpen={isViewCertModalOpen}
               onClose={() => setIsViewCertModalOpen(false)}
               certificate={certData?.getCertificatesByApplicationId[0]}
               loading={certLoading}
               error={certError}
            />
         )}
         <HistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            history={application.history}
         />
         {application.status === 'Released' && application.applicationType === 'Private Tree Plantation Registration' && (
            <ApplicantSignatureModal
               isOpen={isSignatureModalOpen}
               onClose={() => setIsSignatureModalOpen(false)}
               certificate={certData?.getCertificatesByApplicationId[0]}
               onSignComplete={() => {
                  refetch();
               }}
            />
         )}

         {/* Confirmation Dialogs */}
         <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Delete Draft Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to delete this draft application? This action cannot be undone.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={() => {
                        onDelete(application);
                        setShowDeleteConfirm(false);
                     }}
                     className="bg-red-600 hover:bg-red-700 text-white"
                  >
                     Delete
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showUnsubmitConfirm} onOpenChange={setShowUnsubmitConfirm}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Unsubmit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to unsubmit this application? This will return it to draft status.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={() => {
                        onUnsubmit(application);
                        setShowUnsubmitConfirm(false);
                     }}
                     className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  >
                     Unsubmit
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showResubmitConfirm} onOpenChange={setShowResubmitConfirm}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Resubmit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to resubmit this application? Please ensure you have addressed all the remarks.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={() => {
                        onResubmit(application);
                        setShowResubmitConfirm(false);
                     }}
                     className="bg-green-600 hover:bg-green-700 text-white"
                  >
                     Resubmit
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Submit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to submit this application? Please ensure all information is correct.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={() => {
                        onSubmit(application);
                        setShowSubmitConfirm(false);
                     }}
                     className="bg-green-600 hover:bg-green-700 text-white"
                  >
                     Submit
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showUndoRenewalConfirm} onOpenChange={setShowUndoRenewalConfirm}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Undo Renewal</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to undo the renewal of this application? This will revert it to its previous status.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={async () => {
                        try {
                           await undoRenewal({ variables: { applicationId: application.id } });
                           toast.success('Renewal undone successfully');
                        } catch (error) {
                           toast.error('Failed to undo renewal');
                        }
                        setShowUndoRenewalConfirm(false);
                     }}
                     className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                     Undo Renewal
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </>
   );
};

export default ClientApplicationCard;